import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { VersionControllerService } from '../../api/services/VersionControllerService';
import { getFrontendVersion } from '../../utils/versionCheck';
import { BUILD_COMMIT, BUILD_TIME, formatBuild } from '../../utils/buildInfo';

/**
 * Icon-only PlanBack logo in the mobile header. Tapping it opens an
 * about-dialog with the full logo and the current frontend + backend
 * versions. The backend version is fetched lazily on first open and
 * cached for the lifetime of the dialog instance.
 */
export default function MobileLogoButton() {
  const { t, i18n } = useTranslation(['common']);
  const [open, setOpen] = useState(false);
  const [backendVersion, setBackendVersion] = useState<string | null>(null);
  const [backendBuildInfo, setBackendBuildInfo] = useState<{ commit?: string; time?: string } | null>(null);
  const [versionError, setVersionError] = useState(false);
  const frontendVersion = getFrontendVersion();
  const frontendBuild = formatBuild(BUILD_COMMIT, BUILD_TIME, i18n.language);
  const backendBuild = formatBuild(backendBuildInfo?.commit, backendBuildInfo?.time, i18n.language);

  useEffect(() => {
    if (!open || backendVersion !== null || versionError) {
      return;
    }
    let cancelled = false;
    VersionControllerService.getVersion()
      .then((info) => {
        if (cancelled) return;
        const v = info?.backendVersion;
        if (v) {
          setBackendVersion(v);
          setBackendBuildInfo({ commit: info.buildCommit, time: info.buildTime });
        } else {
          setVersionError(true);
        }
      })
      .catch(() => {
        if (!cancelled) setVersionError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, backendVersion, versionError]);

  const footer = () => (
    <div className="flex justify-end p-3">
      <ButtonComponent
        cssClass="e-primary"
        ref={(btn: any) => {
          if (btn && btn.element) {
            btn.element.onclick = () => setOpen(false);
          }
        }}
      >
        {t('actions.close')}
      </ButtonComponent>
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="shrink-0 inline-flex items-center justify-center min-h-12 min-w-12 active:opacity-80"
        aria-label="PlanBack"
      >
        <img src="/Logo.png" alt="PlanBack" className="h-8 w-auto" />
      </button>

      {open && (
        <DialogComponent
          visible={true}
          isModal={true}
          showCloseIcon={true}
          closeOnEscape={true}
          enableResize={false}
          allowDragging={false}
          width="320px"
          height="auto"
          target="body"
          animationSettings={{ effect: 'None' }}
          cssClass="mobile-about-dialog"
          zIndex={9500}
          header={t('mobile.about.title')}
          close={() => setOpen(false)}
          footerTemplate={footer}
        >
          <div className="flex flex-col items-center p-4 gap-4">
            <img src="/PlanBack_r200.png" alt="PlanBack" className="w-full max-w-[220px] h-auto" />
            <div className="text-sm text-gray-700 text-center">
              <div>
                {t('mobile.about.frontend')}: <span className="font-mono">{frontendVersion}</span>
              </div>
              {frontendBuild && (
                <div className="text-gray-500">
                  {t('mobile.about.build')} <span className="font-mono">{frontendBuild}</span>
                </div>
              )}
              <div>
                {t('mobile.about.backend')}:{' '}
                <span className="font-mono">
                  {backendVersion ?? (versionError ? t('mobile.about.unknown') : '…')}
                </span>
              </div>
              {backendBuild && (
                <div className="text-gray-500">
                  {t('mobile.about.build')} <span className="font-mono">{backendBuild}</span>
                </div>
              )}
            </div>
          </div>
        </DialogComponent>
      )}
    </>
  );
}
